/**
 * Shipping Protection — cart-drawer add-on (Dawn adaptation).
 *
 * Implements the proven model from SHIPPING-PROTECTION-SETUP.md:
 *
 *  - The snippet in sections/cart-drawer.liquid renders the toggle with an
 *    INLINE `onchange="window.onShippingProtectionToggle(this)"` and computes
 *    `checked` + `data-line-item-key` SERVER-SIDE from cart.items. Inline
 *    handlers are part of the re-rendered HTML, so they keep working after
 *    every drawer re-render / close-reopen with zero JS rebinding — this is
 *    what fixes "can't toggle after reopening the drawer".
 *
 *  - After any change we dispatch the theme's `cart:refresh`, which is Dawn's
 *    equivalent of the doc's `refreshCartContents`: `CartDrawer._onCartRefresh`
 *    re-fetches the section and fully re-renders the drawer (snippet included,
 *    server-side), and `CartCount` re-reads /cart.js. This bypasses the theme's
 *    `_onCartChanged` partial-swap + its built-in 1250ms delay — that delay is
 *    what made auto-add "take too long".
 *
 *  - One-time auto-add (opt-out model) gated by a localStorage flag, exactly
 *    per the doc: add once per browser, then never nag again.
 *
 * No custom element, no addEventListener for the toggle — deliberately, so the
 * handler can never be lost across the drawer's Shadow-DOM slot re-rendering.
 */

var INIT_KEY = "shipping_protection_init";

function spRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
}

// Dawn-native "refreshCartContents": full drawer re-render + count resync,
// without the fragile/slow `_onCartChanged` path.
function spRefreshCart() {
  document.dispatchEvent(new CustomEvent("cart:refresh"));
}

// Swap the toggle label to the localized loading text while a request is in
// flight. The subsequent `cart:refresh` fully re-renders the drawer from the
// server, which restores the normal label (with the live, dynamic price).
function spSetLoadingLabel(input, type) {
  var box =
    (input && input.closest && input.closest("#shipping-protection")) ||
    document.querySelector("#shipping-protection");
  if (!box) return;
  var label = box.querySelector('label[for="shipping-protection-checkbox"]');
  if (!label) return;
  var text = type === "remove" ? box.dataset.removing : box.dataset.adding;
  if (text) label.textContent = text;
}

// Inline onchange target — MUST be global so the snippet's
// `onchange="window.onShippingProtectionToggle(this)"` resolves.
window.onShippingProtectionToggle = async function (input) {
  if (input.dataset.busy === "1") return;
  input.dataset.busy = "1";
  input.disabled = true;
  spSetLoadingLabel(input, input.checked ? "add" : "remove");

  var root = spRoot();

  try {
    if (input.checked) {
      await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items: [{ id: Number(input.value), quantity: 1 }] })
      });
    } else {
      await fetch(`${root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: input.dataset.lineItemKey, quantity: 0 })
      });
    }
  } catch (e) {
    /* server is the source of truth; the refresh below re-renders real state */
  }

  // Re-render from the server so `checked` / `data-line-item-key` resync.
  spRefreshCart();
};

// One-time auto-add (opt-out). The snippet only exists when the feature is
// enabled AND the cart is non-empty (Dawn's empty-cart drawer has no footer),
// so the element's presence is itself the "don't add to an empty cart" gate.
async function spInitAutoAdd() {
  var input = document.querySelector("#shipping-protection-checkbox");
  if (!input || !input.value) return; // feature off / not on page / empty cart
  if (input.checked) return; // already in the cart

  var done;
  try {
    done = window.localStorage.getItem(INIT_KEY) === "1";
  } catch (e) {
    done = false;
  }
  if (done) return; // already auto-added once on this browser — never nag again

  spSetLoadingLabel(input, "add");

  try {
    await fetch(`${spRoot()}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({ items: [{ id: Number(input.value), quantity: 1 }] })
    });
    try {
      window.localStorage.setItem(INIT_KEY, "1");
    } catch (e) {
      /* localStorage unavailable — auto-add may repeat next visit, acceptable */
    }
    spRefreshCart();
  } catch (e) {
    // Re-render so the loading label reverts to the normal text; the toggle
    // still works manually.
    spRefreshCart();
  }
}

// Run on load, and whenever the cart changes (so the one-time auto-add can fire
// the moment the cart first becomes non-empty and the snippet appears). The
// localStorage flag + "already checked" guard keep it idempotent — no loop.
if (!window.__shippingProtectionInit) {
  window.__shippingProtectionInit = true;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", spInitAutoAdd);
  } else {
    spInitAutoAdd();
  }

  // `cart:change` fires after the theme has re-rendered the drawer footer, so
  // the snippet is present in the DOM by the time this runs.
  document.addEventListener("cart:change", spInitAutoAdd);
}
